import React, { useState, useEffect } from 'react';
import CollapsibleSection from './CollapsibleSection';
import ConfirmationModal from './ConfirmationModal';
import { useToast } from './Toast';
import { FirebaseDiagnosisPrescriptionService, FirebasePrescriptionRecord } from '../services/firebase/FirebaseDiagnosisPrescriptionService';
import { FirebasePatientService, Patient } from '../services/firebase/FirebasePatientService';

interface Prescription {
  id: string;
  patientId: string;
  patientName: string;
  medication: string;
  dosage: string;
  prescribedDate: string;
  lastFilled: string;
  daysSupply: number;
  refillsRemaining: number;
  pharmacy: string;
  pharmacyPhone: string;
  status: 'active' | 'due-soon' | 'due-now' | 'discontinued';
  reminderType: '30-day' | '90-day';
  prescribedBy?: string;
}

interface PrescriptionsPageProps {
  userType: 'client' | 'provider';
  currentUser?: any;
  providerId?: string;
  clientData?: any; // Include client profile data with preferred pharmacy
  onPharmacyChangeClick?: () => void; // Callback to switch to pharmacy update section
}

// Available medications for prescription - Comprehensive list
const availableMedications = [
  // ADHD Medications - Non-Stimulants
  {
    name: 'Atomoxetine (Strattera)',
    category: 'ADHD Non-Stimulant',
    commonDosages: ['10mg', '18mg', '25mg', '40mg', '60mg', '80mg', '100mg'],
    instructions: 'Take once or twice daily with or without food'
  },
  {
    name: 'Guanfacine (Intuniv)',
    category: 'ADHD Non-Stimulant',
    commonDosages: ['1mg', '2mg', '3mg', '4mg'],
    instructions: 'Take once daily at bedtime'
  },
  {
    name: 'Clonidine',
    category: 'ADHD Non-Stimulant',
    commonDosages: ['0.1mg', '0.2mg', '0.3mg'],
    instructions: 'Take as directed, usually twice daily'
  },
  {
    name: 'Viloxazine (Qelbree)',
    category: 'ADHD Non-Stimulant',
    commonDosages: ['100mg', '150mg', '200mg'],
    instructions: 'Take once daily in the morning with food'
  },

  // ADHD Medications - Stimulants
  {
    name: 'Methylphenidate (Ritalin)',
    category: 'ADHD Stimulant',
    commonDosages: ['5mg', '10mg', '20mg'],
    instructions: 'Take 2-3 times daily, 30-45 minutes before meals'
  },
  {
    name: 'Methylphenidate ER (Concerta)',
    category: 'ADHD Stimulant',
    commonDosages: ['18mg', '27mg', '36mg', '54mg'],
    instructions: 'Take once daily in the morning with or without food'
  },
  {
    name: 'Amphetamine Salts (Adderall)',
    category: 'ADHD Stimulant',
    commonDosages: ['5mg', '10mg', '15mg', '20mg', '25mg', '30mg'],
    instructions: 'Take 1-3 times daily, first dose upon awakening'
  },
  {
    name: 'Lisdexamfetamine (Vyvanse)',
    category: 'ADHD Stimulant',
    commonDosages: ['20mg', '30mg', '40mg', '50mg', '60mg', '70mg'],
    instructions: 'Take once daily in the morning'
  },

  // Antidepressants - SSRIs
  {
    name: 'Sertraline (Zoloft)',
    category: 'SSRI Antidepressant',
    commonDosages: ['25mg', '50mg', '100mg', '150mg', '200mg'],
    instructions: 'Take once daily, preferably in the morning'
  },
  {
    name: 'Fluoxetine (Prozac)',
    category: 'SSRI Antidepressant',
    commonDosages: ['10mg', '20mg', '40mg', '60mg'],
    instructions: 'Take once daily in the morning'
  },
  {
    name: 'Escitalopram (Lexapro)',
    category: 'SSRI Antidepressant',
    commonDosages: ['5mg', '10mg', '20mg'],
    instructions: 'Take once daily, morning or evening'
  },
  {
    name: 'Paroxetine (Paxil)',
    category: 'SSRI Antidepressant',
    commonDosages: ['10mg', '20mg', '30mg', '40mg'],
    instructions: 'Take once daily, preferably in the morning'
  },
  {
    name: 'Citalopram (Celexa)',
    category: 'SSRI Antidepressant',
    commonDosages: ['10mg', '20mg', '40mg'],
    instructions: 'Take once daily, morning or evening'
  },

  // Antidepressants - SNRIs
  {
    name: 'Venlafaxine (Effexor)',
    category: 'SNRI Antidepressant',
    commonDosages: ['37.5mg', '75mg', '150mg', '225mg'],
    instructions: 'Take with food, same time each day'
  },
  {
    name: 'Duloxetine (Cymbalta)',
    category: 'SNRI Antidepressant',
    commonDosages: ['20mg', '30mg', '60mg', '120mg'],
    instructions: 'Take once daily, swallow whole, do not crush'
  },

  // Antidepressants - Atypical
  {
    name: 'Bupropion (Wellbutrin)',
    category: 'Atypical Antidepressant',
    commonDosages: ['75mg', '100mg', '150mg', '200mg', '300mg'],
    instructions: 'Take twice daily, avoid evening doses'
  },
  {
    name: 'Trazodone',
    category: 'Atypical Antidepressant',
    commonDosages: ['25mg', '50mg', '100mg', '150mg', '300mg'],
    instructions: 'Take with food, usually at bedtime'
  },
  {
    name: 'Mirtazapine (Remeron)',
    category: 'Atypical Antidepressant',
    commonDosages: ['7.5mg', '15mg', '30mg', '45mg'],
    instructions: 'Take once daily at bedtime'
  },

  // Anti-Anxiety Medications - Benzodiazepines
  {
    name: 'Lorazepam (Ativan)',
    category: 'Benzodiazepine',
    commonDosages: ['0.5mg', '1mg', '2mg'],
    instructions: 'Take as needed for anxiety, potential for dependence'
  },
  {
    name: 'Alprazolam (Xanax)',
    category: 'Benzodiazepine',
    commonDosages: ['0.25mg', '0.5mg', '1mg', '2mg'],
    instructions: 'Take as needed for anxiety, potential for dependence'
  },
  {
    name: 'Clonazepam (Klonopin)',
    category: 'Benzodiazepine',
    commonDosages: ['0.5mg', '1mg', '2mg'],
    instructions: 'Take as directed, potential for dependence'
  },

  // Anti-Anxiety Medications - Non-Benzodiazepine
  {
    name: 'Buspirone (BuSpar)',
    category: 'Non-Benzodiazepine Anxiolytic',
    commonDosages: ['5mg', '7.5mg', '10mg', '15mg', '30mg'],
    instructions: 'Take 2-3 times daily with food'
  },
  {
    name: 'Hydroxyzine (Vistaril)',
    category: 'Non-Benzodiazepine Anxiolytic',
    commonDosages: ['10mg', '25mg', '50mg'],
    instructions: 'Take as needed for anxiety, may cause drowsiness'
  },

  // Mood Stabilizers
  {
    name: 'Lithium',
    category: 'Mood Stabilizer',
    commonDosages: ['300mg', '450mg', '600mg'],
    instructions: 'Take with food, requires regular blood monitoring'
  },
  {
    name: 'Lamotrigine (Lamictal)',
    category: 'Mood Stabilizer',
    commonDosages: ['25mg', '50mg', '100mg', '150mg', '200mg'],
    instructions: 'Take once or twice daily, titrate slowly'
  },
  {
    name: 'Valproic Acid (Depakote)',
    category: 'Mood Stabilizer',
    commonDosages: ['125mg', '250mg', '500mg'],
    instructions: 'Take with food, requires regular blood monitoring'
  },

  // Sleep Medications
  {
    name: 'Zolpidem (Ambien)',
    category: 'Sleep Aid',
    commonDosages: ['5mg', '10mg'],
    instructions: 'Take immediately before bedtime, 7-8 hours before awakening'
  },
  {
    name: 'Eszopiclone (Lunesta)',
    category: 'Sleep Aid',
    commonDosages: ['1mg', '2mg', '3mg'],
    instructions: 'Take immediately before bedtime'
  },
  {
    name: 'Melatonin',
    category: 'Sleep Aid',
    commonDosages: ['1mg', '3mg', '5mg', '10mg'],
    instructions: 'Take 30 minutes before desired bedtime'
  },

  // Trauma/PTSD Specific
  {
    name: 'Prazosin',
    category: 'PTSD/Trauma',
    commonDosages: ['1mg', '2mg', '5mg'],
    instructions: 'Take at bedtime for nightmares, monitor blood pressure'
  },
  {
    name: 'Propranolol',
    category: 'PTSD/Trauma',
    commonDosages: ['10mg', '20mg', '40mg', '80mg'],
    instructions: 'Take as directed, may be used for performance anxiety'
  },

  // Antipsychotics - Atypical
  {
    name: 'Aripiprazole (Abilify)',
    category: 'Atypical Antipsychotic',
    commonDosages: ['2mg', '5mg', '10mg', '15mg', '20mg', '30mg'],
    instructions: 'Take once daily with or without food'
  },
  {
    name: 'Quetiapine (Seroquel)',
    category: 'Atypical Antipsychotic',
    commonDosages: ['25mg', '50mg', '100mg', '200mg', '300mg', '400mg'],
    instructions: 'Take as directed, may cause drowsiness'
  },
  {
    name: 'Risperidone (Risperdal)',
    category: 'Atypical Antipsychotic',
    commonDosages: ['0.25mg', '0.5mg', '1mg', '2mg', '3mg', '4mg'],
    instructions: 'Take once or twice daily with or without food'
  }
];

// Mock prescription data
const mockPrescriptions: Prescription[] = [
  {
    id: '1',
    patientId: '1',
    patientName: 'Sarah Johnson',
    medication: 'Atomoxetine (Strattera) 40mg',
    dosage: 'Once daily in morning',
    prescribedDate: '2024-08-15',
    lastFilled: '2024-08-15',
    daysSupply: 30,
    refillsRemaining: 3,
    pharmacy: 'CVS Pharmacy - Main St',
    pharmacyPhone: '(555) 123-4567',
    status: 'active',
    reminderType: '30-day',
    prescribedBy: 'Dr. Tara Miller'
  },
  {
    id: '2',
    patientId: '1',
    patientName: 'Sarah Johnson',
    medication: 'Sertraline (Zoloft) 50mg',
    dosage: 'Once daily in morning',
    prescribedDate: '2024-07-20',
    lastFilled: '2024-08-10',
    daysSupply: 30,
    refillsRemaining: 5,
    pharmacy: 'Walgreens - Oak Ave',
    pharmacyPhone: '(555) 234-5678',
    status: 'due-soon',
    reminderType: '30-day',
    prescribedBy: 'Dr. Tara Miller'
  },
  {
    id: '3',
    patientId: '2',
    patientName: 'Michael Chen',
    medication: 'Lisdexamfetamine (Vyvanse) 30mg',
    dosage: 'Once daily in the morning',
    prescribedDate: '2024-06-10',
    lastFilled: '2024-06-10',
    daysSupply: 30,
    refillsRemaining: 2,
    pharmacy: 'CVS Pharmacy - Main St',
    pharmacyPhone: '(555) 123-4567',
    status: 'due-now',
    reminderType: '30-day',
    prescribedBy: 'Dr. Tara Miller'
  },
  {
    id: '4',
    patientId: '2',
    patientName: 'Michael Chen',
    medication: 'Buspirone (BuSpar) 15mg',
    dosage: 'Twice daily with food',
    prescribedDate: '2024-07-01',
    lastFilled: '2024-08-15',
    daysSupply: 30,
    refillsRemaining: 4,
    pharmacy: 'CVS Pharmacy - Main St',
    pharmacyPhone: '(555) 123-4567',
    status: 'active',
    reminderType: '30-day',
    prescribedBy: 'Dr. Tara Miller'
  },
  {
    id: '5',
    patientId: '3',
    patientName: 'Emily Rodriguez',
    medication: 'Escitalopram (Lexapro) 10mg',
    dosage: 'Once daily in morning',
    prescribedDate: '2024-05-15',
    lastFilled: '2024-08-15',
    daysSupply: 90,
    refillsRemaining: 4,
    pharmacy: 'Rite Aid - Central Plaza',
    pharmacyPhone: '(555) 345-6789',
    status: 'active',
    reminderType: '90-day',
    prescribedBy: 'Dr. Tara Miller'
  },
  {
    id: '6',
    patientId: '3',
    patientName: 'Emily Rodriguez',
    medication: 'Trazodone 50mg',
    dosage: 'At bedtime as needed for sleep',
    prescribedDate: '2024-06-01',
    lastFilled: '2024-08-20',
    daysSupply: 30,
    refillsRemaining: 3,
    pharmacy: 'Rite Aid - Central Plaza',
    pharmacyPhone: '(555) 345-6789',
    status: 'active',
    reminderType: '30-day',
    prescribedBy: 'Dr. Tara Miller'
  }
];

const PrescriptionsPage: React.FC<PrescriptionsPageProps> = ({ userType, currentUser, providerId, clientData, onPharmacyChangeClick }) => {
  const toast = useToast();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [showNewPrescriptionForm, setShowNewPrescriptionForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalMessage, setEditModalMessage] = useState('');
  const [selectedMedication, setSelectedMedication] = useState('');
  const [selectedDosage, setSelectedDosage] = useState('');
  const [selectedPatient, setSelectedPatient] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [medicationSearchTerm, setMedicationSearchTerm] = useState('');
  const [patientNames, setPatientNames] = useState<Record<string, string>>({});
  const [realPatients, setRealPatients] = useState<Patient[]>([]);

  // Additional prescription form fields
  const [quantity, setQuantity] = useState('');
  const [refills, setRefills] = useState('');
  const [daysSupply, setDaysSupply] = useState('');
  const [instructions, setInstructions] = useState('');
  const [prescriptionNotes, setPrescriptionNotes] = useState('');
  const [genericSubstitution, setGenericSubstitution] = useState(true);

  // Load real prescriptions from Firestore
  useEffect(() => {
    const pid = providerId || currentUser?.id;
    if (!pid) return;

    // Load patients for name lookup
    FirebasePatientService.getAllPatients(pid).then(patients => {
      setRealPatients(patients);
      const nameMap: Record<string, string> = {};
      patients.forEach(p => { nameMap[p.id] = `${p.firstName} ${p.lastName}`; });
      setPatientNames(nameMap);
    }).catch(err => console.error('Failed to load patients:', err));

    // Subscribe to real prescriptions
    const unsub = FirebaseDiagnosisPrescriptionService.subscribeToProviderPrescriptions(
      pid,
      (firestoreRx: FirebasePrescriptionRecord[]) => {
        const mapped: Prescription[] = firestoreRx.map(rx => ({
          id: rx.id,
          patientId: rx.patientId,
          patientName: '', // filled below via patientNames
          medication: `${rx.medicationName} ${rx.dosage}`,
          dosage: rx.frequency,
          prescribedDate: rx.prescribedDate,
          lastFilled: rx.startDate || rx.prescribedDate,
          daysSupply: 30,
          refillsRemaining: rx.refillsRemaining ?? 0,
          pharmacy: rx.pharmacy || 'Not specified',
          pharmacyPhone: '',
          status: rx.status === 'active' ? 'active' : rx.status === 'discontinued' ? 'discontinued' : 'active',
          reminderType: '30-day',
          prescribedBy: rx.prescribedBy,
        }));
        setPrescriptions(mapped);
      }
    );
    return () => unsub();
  }, [providerId, currentUser?.id]);

  // Update patient names in prescriptions when patientNames map changes
  useEffect(() => {
    if (Object.keys(patientNames).length === 0) return;
    setPrescriptions(prev => prev.map(rx => ({
      ...rx,
      patientName: patientNames[rx.patientId] || rx.patientName || 'Unknown Patient'
    })));
  }, [patientNames]);

  // Get unique medication categories for filter dropdown
  const medicationCategories = Array.from(new Set(availableMedications.map(med => med.category))).sort();

  // Filter medications based on category and search term
  const filteredMedications = availableMedications.filter(medication => {
    const matchesCategory = !selectedCategory || medication.category === selectedCategory;
    const matchesSearch = !medicationSearchTerm ||
      medication.name.toLowerCase().includes(medicationSearchTerm.toLowerCase()) ||
      medication.category.toLowerCase().includes(medicationSearchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Filter prescriptions based on user type and update with client's preferred pharmacy
  const userPrescriptions = userType === 'client'
    ? prescriptions.filter(p => p.patientId === currentUser?.id).map(prescription => {
        // Use client's preferred pharmacy if available
        if (clientData?.preferredPharmacy) {
          return {
            ...prescription,
            pharmacy: clientData.preferredPharmacy.name,
            pharmacyPhone: clientData.preferredPharmacy.phone
          };
        }
        return prescription;
      })
    : prescriptions;

  const filteredPrescriptions = userPrescriptions.filter(prescription =>
    prescription.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prescription.medication.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const duePrescriptions = userPrescriptions.filter(p => p.status === 'due-now' || p.status === 'due-soon');

  /**
   * MyClinicCore does NOT transmit prescriptions electronically. There is no
   * Surescripts or DrFirst integration — nothing here reaches a pharmacy.
   *
   * This previously showed a "Prescription Sent" toast and did nothing else,
   * which told a prescriber their patient's medication was on its way when it
   * was not. Until real e-prescribing exists, the honest useful action is to
   * put the details where the provider can phone or fax them in.
   */
  const copyPrescriptionForPharmacy = async (prescription: Prescription) => {
    const details = [
      `Patient: ${prescription.patientName}`,
      `Medication: ${prescription.medication}`,
      `Dosage: ${prescription.dosage}`,
      `Days supply: ${prescription.daysSupply}`,
      `Refills remaining: ${prescription.refillsRemaining}`,
      prescription.prescribedBy ? `Prescriber: ${prescription.prescribedBy}` : null,
      `Pharmacy: ${prescription.pharmacy}`,
      `Pharmacy phone: ${prescription.pharmacyPhone}`,
    ]
      .filter(Boolean)
      .join('\n');

    const callToAction = `Call ${prescription.pharmacy} at ${prescription.pharmacyPhone} to place it. MyClinicCore does not send prescriptions electronically.`;

    try {
      await navigator.clipboard.writeText(details);
      toast.success('Copied for pharmacy', `${details}\n\n${callToAction}`);
    } catch {
      // Clipboard can be blocked; the details are still on screen.
      toast.warning('Copy this to your pharmacy', `${details}\n\n${callToAction}`);
    }
  };

  const handleNewPrescription = async () => {
    // Validate required fields
    const requiredFields = [];
    if (userType === 'provider' && !selectedPatient) requiredFields.push('Patient');
    if (!selectedMedication) requiredFields.push('Medication');
    if (!selectedDosage) requiredFields.push('Dosage');
    if (!quantity) requiredFields.push('Quantity');
    if (!daysSupply) requiredFields.push('Days Supply');
    if (!refills) requiredFields.push('Refills');
    if (!instructions.trim()) requiredFields.push('Patient Instructions');

    if (requiredFields.length > 0) {
      toast.warning('Missing Fields', `Please fill in the following required fields:\n• ${requiredFields.join('\n• ')}`);
      return;
    }

    // Validate logical constraints
    if (parseInt(quantity) <= 0) {
      toast.warning('Invalid Quantity', 'Quantity must be greater than 0');
      return;
    }

    if (parseInt(daysSupply) <= 0) {
      toast.warning('Invalid Days Supply', 'Days Supply must be greater than 0');
      return;
    }

    const pid = providerId || currentUser?.id;
    if (!pid) {
      toast.error('Error', 'Provider ID not available');
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      await FirebaseDiagnosisPrescriptionService.addPrescription({
        medicationName: selectedMedication,
        dosage: selectedDosage,
        frequency: instructions,
        prescribedDate: today,
        prescribedBy: currentUser?.name || 'Provider',
        providerId: pid,
        patientId: selectedPatient,
        status: 'active',
        startDate: today,
        refillsRemaining: parseInt(refills) || 0,
        pharmacy: clientData?.preferredPharmacy?.name || '',
        notes: prescriptionNotes || undefined,
      });
      toast.success('Prescription Created', `${selectedMedication} ${selectedDosage} prescribed successfully.`);
    } catch (error) {
      console.error('Failed to create prescription:', error);
      toast.error('Error', 'Failed to create prescription. Please try again.');
      return;
    }

    setShowNewPrescriptionForm(false);
    resetFormFields();
  };

  const getSelectedMedicationInfo = () => {
    return availableMedications.find(med => med.name === selectedMedication);
  };

  // Reset form fields when filters change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setSelectedMedication(''); // Clear selected medication when category changes
    setSelectedDosage(''); // Clear selected dosage
    setInstructions(''); // Clear auto-filled instructions
  };

  const handleMedicationSearchChange = (searchTerm: string) => {
    setMedicationSearchTerm(searchTerm);
    setSelectedMedication(''); // Clear selected medication when search changes
    setSelectedDosage(''); // Clear selected dosage
    setInstructions(''); // Clear auto-filled instructions
  };

  // Reset all form fields
  const resetFormFields = () => {
    setSelectedMedication('');
    setSelectedDosage('');
    setSelectedPatient('');
    setSelectedCategory('');
    setMedicationSearchTerm('');
    setQuantity('');
    setRefills('');
    setDaysSupply('');
    setInstructions('');
    setPrescriptionNotes('');
    setGenericSubstitution(true);
  };

  // Auto-fill instructions when medication is selected
  const handleMedicationChange = (medicationName: string) => {
    setSelectedMedication(medicationName);
    setSelectedDosage(''); // Clear dosage when medication changes

    if (medicationName) {
      const medicationInfo = availableMedications.find(med => med.name === medicationName);
      if (medicationInfo) {
        setInstructions(medicationInfo.instructions);
      }
    } else {
      setInstructions('');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, color: '#f9fafb' }}>
          {userType === 'client' ? 'My Prescriptions' : 'Prescription Management'}
        </h2>
        {userType === 'provider' && (
          <button
            className="btn btn-primary"
            style={{ backgroundColor: '#3b82f6' }}
            onClick={() => setShowNewPrescriptionForm(!showNewPrescriptionForm)}
          >
            + New Prescription
          </button>
        )}
      </div>


      {/* Preferred Pharmacy Information - Client View */}
      {userType === 'client' && clientData?.preferredPharmacy && (
        <div style={{
          background: '#E6F3FF',
          border: '2px solid #3B82F6',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#1E40AF', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Your Preferred Pharmacy
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem', color: '#1E40AF' }}>
            <span style={{ fontWeight: 'bold' }}>Name:</span>
            <span>{clientData.preferredPharmacy.name}</span>
            <span style={{ fontWeight: 'bold' }}>Address:</span>
            <span>{clientData.preferredPharmacy.address}</span>
            <span style={{ fontWeight: 'bold' }}>Phone:</span>
            <span>{clientData.preferredPharmacy.phone}</span>
            <span style={{ fontWeight: 'bold' }}>Hours:</span>
            <span>{clientData.preferredPharmacy.hours}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
            <p style={{ margin: 0, color: '#1E40AF', fontSize: '0.875rem', fontStyle: 'italic' }}>
              All your prescriptions will be sent to this pharmacy automatically.
            </p>
            {onPharmacyChangeClick && (
              <button
                onClick={onPharmacyChangeClick}
                style={{
                  background: '#D4A017',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                Change Pharmacy
              </button>
            )}
          </div>
        </div>
      )}

      {/* No Preferred Pharmacy Warning - Client View */}
      {userType === 'client' && !clientData?.preferredPharmacy && (
        <div style={{
          background: '#FEF3C7',
          border: '2px solid #F59E0B',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400E' }}>
            No Preferred Pharmacy Selected
          </h4>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0, color: '#78350F', fontSize: '0.875rem', flex: 1 }}>
              Please select a preferred pharmacy. This will ensure your prescriptions are sent to the right place.
            </p>
            {onPharmacyChangeClick && (
              <button
                onClick={onPharmacyChangeClick}
                style={{
                  background: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginLeft: '1rem'
                }}
              >
                Select Pharmacy
              </button>
            )}
          </div>
        </div>
      )}

      {/* New Prescription Form - Provider Only */}
      {userType === 'provider' && showNewPrescriptionForm && (
        <CollapsibleSection
          title="Create New Prescription"
          variant="success"
          defaultExpanded={true}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Patient</label>
              <select 
                className="form-input"
                value={selectedPatient}
                onChange={(e) => setSelectedPatient(e.target.value)}
              >
                <option value="">Select patient...</option>
                {realPatients.map(p => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Medication</label>

              {/* Category Filter */}
              <div style={{ marginBottom: '0.5rem' }}>
                <select
                  className="form-input"
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  style={{ fontSize: '0.875rem' }}
                >
                  <option value="">All Categories</option>
                  {medicationCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Medication Selection */}
              <select
                className="form-input"
                value={selectedMedication}
                onChange={(e) => handleMedicationChange(e.target.value)}
                style={{ maxHeight: '200px', marginBottom: '0.5rem' }}
              >
                <option value="">Select medication...</option>
                {filteredMedications.map(med => (
                  <option key={med.name} value={med.name}>
                    {med.name} ({med.category})
                  </option>
                ))}
              </select>

              {/* Medication Search */}
              <div style={{ marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search medications..."
                  value={medicationSearchTerm}
                  onChange={(e) => handleMedicationSearchChange(e.target.value)}
                  style={{ fontSize: '0.875rem' }}
                />
              </div>

              {/* Results count and clear filters */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.75rem',
                color: '#6B7280',
                marginTop: '0.25rem'
              }}>
                <span>
                  {filteredMedications.length} medication{filteredMedications.length !== 1 ? 's' : ''} found
                </span>
                {(selectedCategory || medicationSearchTerm) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategory('');
                      setMedicationSearchTerm('');
                      setSelectedMedication('');
                      setSelectedDosage('');
                      setInstructions('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3B82F6',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '0.75rem'
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {selectedMedication && (
            <div style={{ marginBottom: '1rem' }}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Dosage</label>
                <select
                  className="form-input"
                  value={selectedDosage}
                  onChange={(e) => setSelectedDosage(e.target.value)}
                >
                  <option value="">Select dosage...</option>
                  {getSelectedMedicationInfo()?.commonDosages.map(dosage => (
                    <option key={dosage} value={dosage}>{dosage}</option>
                  ))}
                </select>
              </div>

              {/* Additional Prescription Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="30"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Days Supply</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="30"
                    value={daysSupply}
                    onChange={(e) => setDaysSupply(e.target.value)}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Refills</label>
                  <select
                    className="form-input"
                    value={refills}
                    onChange={(e) => setRefills(e.target.value)}
                  >
                    <option value="">Select refills...</option>
                    <option value="0">0 (No refills)</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6 (PRN refills)</option>
                  </select>
                </div>
              </div>

              {/* Instructions Field */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Patient Instructions</label>
                <textarea
                  className="form-input"
                  placeholder="Take as directed by your healthcare provider..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                  These instructions will appear on the prescription label
                </div>
              </div>

              {/* Prescription Notes */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Provider Notes (Internal)</label>
                <textarea
                  className="form-input"
                  placeholder="Internal notes about this prescription (not visible to patient)..."
                  value={prescriptionNotes}
                  onChange={(e) => setPrescriptionNotes(e.target.value)}
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Checkboxes */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                  <input
                    type="checkbox"
                    checked={genericSubstitution}
                    onChange={(e) => setGenericSubstitution(e.target.checked)}
                  />
                  Allow generic substitution
                </label>
              </div>

              {/* Medication Info Display */}
              <div style={{
                background: '#f0f9f4',
                border: '1px solid #bbf7d0',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, color: '#166534', fontSize: '0.875rem' }}>
                  <strong>Default Instructions:</strong> {getSelectedMedicationInfo()?.instructions}
                </p>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              className="btn btn-primary"
              style={{ backgroundColor: '#3b82f6' }}
              onClick={handleNewPrescription}
            >
              Create Prescription
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowNewPrescriptionForm(false);
                resetFormFields();
              }}
            >
              Cancel
            </button>
          </div>
        </CollapsibleSection>
      )}

      {/* Due Prescriptions Alert - COLLAPSIBLE */}
      {duePrescriptions.length > 0 && (
        <CollapsibleSection
          title={`${userType === 'client' ? 'Refills Needed' : 'Patient Refills Due'} (${duePrescriptions.length})`}
          variant="danger"
          defaultExpanded={false}
        >
          {duePrescriptions.map(prescription => (
            <div key={prescription.id} style={{ 
              padding: '1rem',
              background: '#fee2e2',
              borderRadius: '0.5rem',
              marginBottom: '0.5rem',
              border: '1px solid #fecaca'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  {userType === 'provider' && (
                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#dc2626' }}>{prescription.patientName}</h4>
                  )}
                  <p style={{ margin: '0 0 0.25rem 0', color: '#7f1d1d', fontSize: '1rem', fontWeight: '600' }}>
                    {prescription.medication}
                  </p>
                  <p style={{ margin: '0 0 0.25rem 0', color: '#991b1b', fontSize: '0.875rem' }}>
                    {prescription.dosage}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#7f1d1d' }}>
                    <span>Last filled: {prescription.lastFilled}</span>
                    <span>Refills remaining: {prescription.refillsRemaining}</span>
                  </div>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#991b1b', fontSize: '0.875rem' }}>
                    Pharmacy: {prescription.pharmacy}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '1rem' }}>
                  {userType === 'provider' && (
                    <>
                      <button
                        className="btn btn-danger"
                        style={{ fontSize: '0.875rem', padding: '0.75rem 1.5rem', whiteSpace: 'nowrap' }}
                        onClick={() => copyPrescriptionForPharmacy(prescription)}
                        title="Copies the prescription details. MyClinicCore does not transmit prescriptions electronically."
                      >
                        Copy for Pharmacy
                      </button>
                      <button
                        className="btn btn-warning"
                        style={{ fontSize: '0.875rem', padding: '0.75rem 1.5rem', whiteSpace: 'nowrap', backgroundColor: '#F59E0B', color: 'white' }}
                        onClick={() => toast.info(
                          'Prior authorization',
                          `MyClinicCore does not submit prior authorizations. Contact the payer directly with:\n\nPatient: ${prescription.patientName}\nMedication: ${prescription.medication}\nDosage: ${prescription.dosage}`
                        )}
                        title="MyClinicCore does not submit prior authorizations. This shows what the payer will ask for."
                      >
                        Prior Auth Details
                      </button>
                    </>
                  )}
                  {userType === 'client' && (
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '0.875rem', padding: '0.75rem 1.5rem', whiteSpace: 'nowrap', backgroundColor: '#3b82f6' }}
                      onClick={() => toast.info('Pharmacy Contact', `Contact your pharmacy: ${prescription.pharmacy} at ${prescription.pharmacyPhone}`)}
                    >
                      Contact Pharmacy
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* All Prescriptions - COLLAPSIBLE */}
      <CollapsibleSection
        title={userType === 'client' ? 'All My Prescriptions' : 'All Patient Prescriptions'}
        variant="default"
        defaultExpanded={false}
      >
        <div style={{ marginBottom: '1rem' }}>
          <input 
            type="text" 
            className="form-input"
            placeholder="Search by medication or patient name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredPrescriptions.map(prescription => (
          <div key={prescription.id} style={{ 
            padding: '1rem',
            background: '#4b5563',
            borderRadius: '0.5rem',
            marginBottom: '0.5rem',
            borderLeft: `4px solid ${prescription.status === 'due-now' ? '#ef4444' : 
                                      prescription.status === 'due-soon' ? '#f59e0b' : '#D4A017'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  {userType === 'provider' && (
                    <h4 style={{ margin: 0, color: '#f9fafb' }}>{prescription.patientName}</h4>
                  )}
                  <span style={{ 
                    background: prescription.status === 'due-now' ? '#ef4444' : 
                                prescription.status === 'due-soon' ? '#f59e0b' : '#D4A017',
                    color: 'white',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '1rem',
                    fontSize: '0.625rem',
                    textTransform: 'uppercase'
                  }}>
                    {prescription.status}
                  </span>
                  <span style={{ 
                    background: prescription.reminderType === '30-day' ? '#3b82f6' : '#8b5cf6',
                    color: 'white',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '1rem',
                    fontSize: '0.625rem'
                  }}>
                    {prescription.reminderType}
                  </span>
                </div>
                
                <p style={{ margin: '0 0 0.25rem 0', color: '#d1d5db', fontSize: '1rem', fontWeight: '600' }}>
                  {prescription.medication}
                </p>
                <p style={{ margin: '0 0 0.25rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                  {prescription.dosage}
                </p>
                
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#d1d5db' }}>
                  <span>Prescribed: {prescription.prescribedDate}</span>
                  <span>Last filled: {prescription.lastFilled}</span>
                  <span>Days supply: {prescription.daysSupply}</span>
                  <span>Refills: {prescription.refillsRemaining}</span>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#d1d5db', marginTop: '0.25rem' }}>
                  <span>Pharmacy: {prescription.pharmacy}</span>
                  {prescription.prescribedBy && (
                    <span>Prescribed by: {prescription.prescribedBy}</span>
                  )}
                </div>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '1rem' }}>
                {userType === 'provider' && (prescription.status === 'due-now' || prescription.status === 'due-soon') && (
                  <>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '0.75rem', padding: '0.5rem 1rem', whiteSpace: 'nowrap', backgroundColor: '#3b82f6' }}
                      onClick={() => copyPrescriptionForPharmacy(prescription)}
                      title="Copies the prescription details. MyClinicCore does not transmit prescriptions electronically."
                    >
                      Copy for Pharmacy
                    </button>
                    <button
                      className="btn btn-warning"
                      style={{ fontSize: '0.75rem', padding: '0.5rem 1rem', whiteSpace: 'nowrap', backgroundColor: '#F59E0B', color: 'white' }}
                      onClick={() => toast.info(
                        'Prior authorization',
                        `MyClinicCore does not submit prior authorizations. Contact the payer directly with:\n\nPatient: ${prescription.patientName}\nMedication: ${prescription.medication}\nDosage: ${prescription.dosage}`
                      )}
                      title="MyClinicCore does not submit prior authorizations. This shows what the payer will ask for."
                    >
                      Prior Auth Details
                    </button>
                  </>
                )}
                {userType === 'client' && (
                  <button 
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}
                    onClick={() => toast.info('Pharmacy Contact', `${prescription.pharmacy} - ${prescription.pharmacyPhone}`)}
                  >
                    Contact Pharmacy
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}
                  onClick={() => {
                    setEditModalMessage(`${userType === 'provider' ? 'Edit prescription' : 'View details'} functionality would be implemented here`);
                    setShowEditModal(true);
                  }}
                >
                  {userType === 'provider' ? 'Edit' : 'Details'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* Edit/Details Modal */}
      <ConfirmationModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onConfirm={() => {}}
        title="Information"
        message={editModalMessage}
        confirmText="OK"
        cancelText=""
        variant="info"
      />
    </div>
  );
};

export default PrescriptionsPage;